import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMediaContext } from 'context/MediaContext'
import { useAuthContext } from 'context/AuthContext'
import { useTimer } from 'react-timer-hook'

import { Alert, Autocomplete, Backdrop, Box, Button, Card, CardContent, FormLabel, Link, Switch, TextField, Typography } from '@mui/material'

import { User } from 'model/User'
import { Season } from 'model/Season'
import { DeckType } from 'model/DeckType'
import { Waiting } from 'model/Waiting'

import * as userRepository from 'repository/UserRepository'
import * as seasonRepository from 'repository/SeasonRepository'
import * as deckTypeRepository from 'repository/DeckTypeRepository'
import * as waitingRepository from 'repository/WaitingRepository'
import * as gameRepository from 'repository/GameRepository'
import * as reportRepository from 'repository/ReportRepository'
import * as chatRepository from 'repository/ChatRepository'


export function Matching() {
  const navigate = useNavigate()
  const { isPc } = useMediaContext()
  const { auth } = useAuthContext()
  const { search } = useLocation()
  
  const query = useMemo(() => new URLSearchParams(search), [search])
  const format = query.get('format') as 'BO1' | 'BO3' | null

  if (!format) navigate('/')

  const [season, setSeason] = useState<Season | null>(null)
  useEffect(() => {
    seasonRepository
      .listFromCache(
        [
          { key: 'end_time', condition: '>=', value: new Date().getTime() }
        ],
        [
          { key: 'end_time', condition: 'asc'}
        ]
      )
      .then(it => {
        const target = it.find(_it => _it.start_time <= new Date().getTime())
        setSeason(target!)
      })
  }, [])

  const [user, setUser] = useState<User | null>(null)
  useEffect(() => {
    if (!auth) return
    userRepository.findFromCache(auth.uid).then(it => setUser(it))
  }, [auth])

  useEffect(() => {
    if (!season) return
    if (!user) return

    gameRepository
      .listFromCache([
          { key: 'season.id', condition: '==', value: season?.id},
          { key: 'player_uuids', condition: 'array-contains', value: user.uuid },
        ],
        [{ key: 'start_time', condition: 'desc' }],
        5
      )
      .then(games => {
        if (games.length > 0) {
          const currentGame = games[0]
          const currentDeckType = currentGame.host.uuid === user.uuid ?
            currentGame.host.deck_type : currentGame.guest.uuid === user.uuid ?
              currentGame.guest.deck_type : null
          setInputDeckType(currentDeckType)
          if (currentDeckType !== null && currentDeckType.main_theme.name === 'その他') {
            setOtherDeckTypeNote(currentDeckType.note)
          }
        }
        const game = games
          .sort((a, b) => a.start_time - b.start_time)
          .find(it => !it.end_time)
        if (game) {
          navigate(`/game?id=${game.id}`)
        }
      })
  }, [season, user])

  const [deckTypeOptions, setDeckTypeOptions] = useState<DeckType[]>([])
  useEffect(() => {
    deckTypeRepository.listFromCache(undefined, [{ key: 'main_theme.sort_key', condition: 'asc' }]).then(its => {
      const ops = its.filter(it => it.main_theme.name !== 'その他')
      const other = its.find(it => it.main_theme.name === 'その他')
      if (other) ops.push(other)
      setDeckTypeOptions(ops)
    })
  }, [])

  /**
   * 入力周りの制御
   */
  const [inputDeckType, setInputDeckType] = useState<DeckType | null>(null)
  const [selectedOther, setSelectedOther] = useState(false)
  const [otherDeckTypeNote, setOtherDeckTypeNote] = useState<String | undefined>(undefined)
  useEffect(() => {
    if (!inputDeckType) {
      setSelectedOther(false)
      setOtherDeckTypeNote(undefined)
      return
    }
    if (inputDeckType.main_theme.name === 'その他') {
      setSelectedOther(true)
    } else {
      setSelectedOther(false)
      setOtherDeckTypeNote(undefined)
    }
  }, [inputDeckType])

  /**
   * マッチング周りの制御
   */
  const [isOverSeason, setIsOverSeason] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [isSearchedWithoutInput, setIsSearchedWithoutInput] = useState(false)
  const [isSearchedWithoutOtherNote, setIsSearchedWithoutOtherNote] = useState(false)
  const [onlyCloseRate, setOnlyCloseRate] = useState(false)
  async function handleSearch() {
    if (season!.end_time <= new Date().getTime()) {
      setIsOverSeason(true)
      return
    }
    if (!inputDeckType) {
      setIsSearchedWithoutInput(true)
      return
    }
    if (inputDeckType.main_theme.name === 'その他' && !otherDeckTypeNote) {
      setIsSearchedWithoutOtherNote(true)
      return
    }
    setIsSearchedWithoutInput(false)
    setIsSearchedWithoutOtherNote(false)
    setIsSearching(true)

    const waitings = await waitingRepository.listFromCache(undefined, [{ key: 'date', condition: 'asc' }])
    if (!waitings) wait()

    const matched = waitings
      .filter(it =>
        it.host.uuid !== user!.uuid &&
        !it.guest &&
        Math.abs(it.host.rate - user!.rate) < (it.only_close_rate || onlyCloseRate ? 100 : 250)
      )
      .some(async it => {
        try {
          const type = Object.assign(inputDeckType!, {note: otherDeckTypeNote})
          const waiting = await waitingRepository.updateGuest(it, user!, type!)
          match(waiting)
          return true // someにtrueを返すことでJavaのforeach breaskの挙動になる
        } catch (e) {
          return false
        }
      })
    
    if (!matched) wait()
  }

  const [waitingId, setWaitingId] = useState<string | null>(null)
  const { seconds, restart } = useTimer(
    { autoStart: false, expiryTimestamp: new Date(), onExpire: handleCancelSearching }
  )
  function wait() {
    const type = Object.assign(inputDeckType!, {note: otherDeckTypeNote})
    const waitingId = waitingRepository.saveWithAssignId(user!, type, onlyCloseRate)
    setWaitingId(waitingId)

    const unsub = waitingRepository.watch(waitingId, (waiting) => {
      if (!waiting) return
      if (waiting.guest) {
        unsub()
        match(waiting)
      }
    })
    const time = new Date()
    time.setSeconds(time.getSeconds() + 60)
    restart(time)
  }

  async function match(waiting: Waiting) {
    const isHost = user!.uuid === waiting.host.uuid
    if (isHost) {
      waitingRepository.remove(waiting.id!)
      reportRepository.save(waiting.id!)
      chatRepository.createRoom(waiting.id!)
      await gameRepository.save(waiting.id!, season!, format!, waiting.host, waiting.guest!)
      navigate(`/game?id=${waiting.id!}`)
    } else {
      const unsub = gameRepository.watch(waiting.id!, game => {
        if (!game) return
        unsub()
        navigate(`/game?id=${game.id!}`)
      })
    }
  }

  function handleCancelSearching() {
    if (waitingId) waitingRepository.remove(waitingId)
    setIsSearching(false)
  }

  function getLabel(deckType: DeckType): string {
    const mainThemeName = deckType.main_theme.name
    const mainThemeKana = deckType.main_theme.kana
    const subThemeName = deckType.sub_theme?.name
    const subThemeKana = deckType.sub_theme?.kana
    let label = mainThemeName
    if (mainThemeKana) label += `（${mainThemeKana}）`
    if (subThemeName) label += ` + ${subThemeName}`
    if (subThemeKana) label += `（${subThemeKana}）`
    return label
  }

  return (
    <>
      <Box sx={isPc ? { pt: 2, px: 10 } : {}}>
        <Alert
          severity='info'
          variant='outlined'
          sx={{ backgroundColor: 'background.default', mb: 3 }}
        >
          <Typography
            variant={isPc ? 'body1' : 'body2'}
          >
            <Link onClick={() => navigate('/rule')}>ルール</Link>を理解した上でご利用ください。
          </Typography>
        </Alert>
        {isSearchedWithoutInput &&
          <Alert
            severity='warning'
            variant='outlined'
            sx={{ backgroundColor: 'background.default', mb: 3 }}
          >
            <Typography
              variant={isPc ? 'body1' : 'body2'}
            >
              デッキタイプを選択してください
            </Typography>
          </Alert>
        }
        {isSearchedWithoutOtherNote &&
          <Alert
            severity='warning'
            variant='outlined'
            sx={{ backgroundColor: 'background.default', mb: 3 }}
          >
            <Typography
              variant={isPc ? 'body1' : 'body2'}
            >
              その他のデッキタイプを入力してください
            </Typography>
          </Alert>
        }
        {isOverSeason &&
          <Alert
            severity='warning'
            variant='outlined'
            sx={{ backgroundColor: 'background.default', mb: 3 }}
          >
            <Typography
              variant={isPc ? 'body1' : 'body2'}
            >
              {season!.name}は終了しました。
            </Typography>
          </Alert>
        }
        <Card variant='outlined' sx={isPc ? {py: 1, px: 10} : {}}>
          <CardContent>
            <Typography>{season?.name ?? 'シーズン期間外です。'}</Typography>
            <Autocomplete
              options={deckTypeOptions}
              getOptionLabel={op => getLabel(op)}
              isOptionEqualToValue={(op, val) => op.id === val.id}
              renderInput={(params) =>
                <TextField
                  {...params}
                  label='使用デッキ'
                  variant='standard'
                  required
                />}
              value={inputDeckType}
              onChange={(e, v) => setInputDeckType(v)}
              sx={{pb: 2}}
            />
            {selectedOther &&
              <>
                <Typography variant='body2' color='text.secondary'>・選択肢にないデッキを使用する場合でのみ、「その他」をお使いください</Typography>
                <Box sx={{py: 2}}>
                  <TextField
                    label='その他のデッキタイプ'
                    variant='standard'
                    fullWidth
                    value={otherDeckTypeNote}
                    onChange={(e) => setOtherDeckTypeNote(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant='body2' color='text.secondary'>・不正防止のため、対戦終了後に表示します</Typography>
                  <Typography variant='body2' color='text.secondary'>・カード名表記のテーマを入力してください</Typography>
                  <Typography variant='body2' color='text.secondary'>・例: 「捕食植物」, 「インフェルノイド」</Typography>
                  <Typography variant='body2' color='text.secondary'>・カード名表記で表せない場合はその限りではありません</Typography>
                  <Typography variant='body2' color='text.secondary'>・例: 「後攻型8軸相剣」, 「メタビート」</Typography>
                </Box>
              </>
            }
            {user?.is_subscriber &&
              <>
                <Switch
                  checked={onlyCloseRate}
                  onChange={e => setOnlyCloseRate(e.target.checked)}
                />
                <FormLabel>レート差100以内のみマッチング</FormLabel>
              </>
            }
            <Button
              variant='outlined'
              onClick={handleSearch}
              size='large'
              fullWidth
              disabled={!season}
              sx={{mt: 2}}
            >
              <Typography
                sx={{ py: 5 }}
              >
                対戦相手を探す
              </Typography>
            </Button>
          </CardContent>
        </Card>
      </Box>

      <Backdrop open={isSearching}>
        <Card variant='outlined' sx={{
          px: isPc ? 10 : 5,
          py: isPc ? 3 : 2
        }}>
          <CardContent>
            <Typography variant='h6' textAlign='center' color='text.secondary'>検索中...</Typography>
            <Typography variant='h1' textAlign='center' color='text.secondary'>{seconds}</Typography>
            <Button
              variant='outlined'
              color='inherit'
              onClick={handleCancelSearching}
              sx={{mt: 3}}
            >
              キャンセル
            </Button>
          </CardContent>
        </Card>
      </Backdrop>
    </>
  )
}
